/**
 * Step4SubmitAttestation.js
 *
 * WHAT: Step 4 of the campaign workflow -- final review of the campaign scope before attestation.
 * WHY:  Before managers can begin reviewing access, an admin must confirm the campaign scope.
 *       This step shows the in-scope users with their assigned attesters. The actual
 *       submit action lives in the parent CampaignWorkflow sidebar: Mark Complete runs a
 *       read-only attester-coverage check and, when it passes, reveals the
 *       Submit-Campaign-for-Attestation button (Yes/Cancel confirmed) that transitions
 *       the campaign to ATTESTATION. The old in-panel green submit button was removed.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Campaign scope-review panel shown as the final checkpoint before a campaign goes live.
 * WHY:  Ensures the admin can review the scope and attester assignments; the submit
 *       action itself is owned by the parent sidebar (see file header).
 *       onUnassignedCountChange reports how many in-scope users have NO attester so the
 *       parent can gray out Mark Complete — these rows come from the prepare endpoint's
 *       unmatched_users list and would otherwise be invisible here (the table used to
 *       build only from the attester-grouped managers list, showing "0 users" for a
 *       fully-unassigned campaign).
 */
const AttestationPhase = ({ campaign, activeStep, onUnassignedCountChange }) => {
  // MSSQL multi-DB: `database_name` is the SQL Server INSTANCE identifier and the
  // physical DBs a grant spans are in `physical_databases`. For MSSQL we relabel
  // the "Database" column to "Instance Name" and add a "DB Names" column (Step 2 parity).
  const isMssql = (campaign?.database_type || '').toUpperCase() === 'MSSQL';
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attestationSubmitted, setAttestationSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [attestationStats, setAttestationStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    approved: 0,
    removed: 0
  });

  /* WHAT: Polls the backend for the current attestation status and completion stats of this campaign.
     WHY: The UI must reflect whether attestation has already been submitted and how many attestations are done. */
  const checkAttestationStatus = async () => {
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`attestation/status/?campaign_id=${campaign.campaign_id}`)
      );
      const data = await response.json();

      if (data.success) {
        setAttestationSubmitted(data.attestation_submitted || false);
        setAttestationStats(data.stats || {
          total: 0,
          completed: 0,
          pending: 0,
          approved: 0,
          removed: 0
        });
      }
    } catch (err) {
      console.error('Error checking attestation status:', err);
    }
  };

  useEffect(() => {
    if (campaign && campaign.campaign_id && activeStep === 4) {
      loadUsers();
      checkAttestationStatus();
    }
  }, [campaign, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  /* WHAT: Fetches the list of in-scope users with their assigned attesters for this campaign.
     WHY: The admin needs to review who will be attested and by whom before submitting the campaign. */
  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (campaign.campaign_id) {
        params.append('campaign_id', campaign.campaign_id);
      }
      // Filter for users in scope only
      params.append('filter_in_scope', 'true');
      // Add timestamp to prevent caching
      params.append('_t', Date.now());

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/prepare/?${params.toString()}`), {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();

      if (data.success) {
        // Aggregate data from all managers and their assigned users
        const assignedUsers = (data.managers || []).flatMap(manager =>
          manager.assigned_users.map(user => ({
            ...user,
            manager_name: manager.manager_name,
            manager_email: manager.manager_email,
            manager_id: manager.manager_id
          }))
        );
        // In-scope users with NO attester (unmatched_users has the same record shape
        // as assigned_users). Shown FIRST with error styling so the problem is
        // impossible to miss; their count grays out the sidebar Mark Complete button.
        const unassignedUsers = (data.unmatched_users || []).map(user => ({
          ...user,
          no_attester: true
        }));
        setUsers([...unassignedUsers, ...assignedUsers]);
        setCurrentPage(1); // Reset to first page when data loads
        if (onUnassignedCountChange) {
          onUnassignedCountChange(unassignedUsers.length);
        }
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* WHAT: Checks whether all attestation decisions have been recorded.
     WHY: Used to display a completion banner once every in-scope user has been attested. */
  const isAttestationComplete = () => {
    return attestationStats.total > 0 && attestationStats.completed === attestationStats.total;
  };

  // Rows flagged no_attester drive the danger banner and the parent's grayed-out
  // Mark Complete button; derived so it can never drift from the table contents.
  const unassignedCount = users.filter(u => u.no_attester).length;

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = users.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(users.length / itemsPerPage);

  /* WHAT: Navigates to a specific page of the user list.
     WHY: The user table is paginated at 25 rows to keep the view manageable for large campaigns. */
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading in-scope users for attestation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert" style={{ display: 'block' }}>
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div>
      {attestationSubmitted && (
                <div className="alert alert-warning mb-4" style={{ display: 'block' }}>
                  <i className="fas fa-lock me-2"></i>
                  <strong>Campaign Locked:</strong> Attestation has been submitted. All workflow steps are now in read-only mode. No changes can be made to the campaign until attestation is complete.
                </div>
              )}

              {/* Attester-coverage problem banner: rows without an attester would never
                  appear in any attestation queue, so the campaign must not go live.
                  Mark Complete in the sidebar is grayed out while this is showing. */}
              {!attestationSubmitted && unassignedCount > 0 && (
                <div className="alert alert-danger mb-4" style={{ display: 'block' }}>
                  <h6 className="alert-heading">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {unassignedCount} in-scope user{unassignedCount === 1 ? '' : 's'} ha{unassignedCount === 1 ? 's' : 've'} no assigned attester
                  </h6>
                  <p className="mb-1">
                    The rows highlighted below would never appear in any attestation queue.
                    This must be corrected before Step 4 can be marked complete — the{' '}
                    <strong>Mark Complete</strong> button stays disabled until every row has an attester.
                  </p>
                  <p className="mb-0">
                    <strong>To fix:</strong> set the primary application's owner (Application Details),
                    or register these accounts under Account Owners / Database Owners, or correct
                    their enterprise-user match — then re-run Step 3 (Attester Assignment).
                  </p>
                </div>
              )}

              {/* How-to-submit hint: the submit action moved to the sidebar
                  (Mark Complete → validation → Submit Campaign for Attestation). */}
              {!attestationSubmitted && unassignedCount === 0 && (
                <div className="alert alert-info mb-4" style={{ display: 'block' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  Every in-scope user below has an attester assigned, so once you have reviewed
                  the list, simply click <strong>Mark Complete</strong> in the sidebar and then
                  confirm with the <strong>Submit Campaign for Attestation</strong> button that
                  appears beneath it to activate the campaign and notify all attesters.
                </div>
              )}

              <div className="mb-3">
                <h6>
                  Total Users for Attestation: <span className="badge bg-primary">{users.length}</span>
                  {unassignedCount > 0 && (
                    <span className="badge bg-danger ms-2">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {unassignedCount} missing attester
                    </span>
                  )}
                </h6>
              </div>

              <div className="table-responsive">
                <table className="table table-hover table-striped">
                  <thead className="table-dark" style={{ fontSize: '0.85rem' }}>
                    <tr>
                      <th>{isMssql ? 'Instance Name' : 'Database'}</th>
                      {isMssql && <th>DB Names</th>}
                      <th>Database User</th>
                      <th>Employee Name/Account Type</th>
                      <th>Employee Department</th>
                      <th>Primary Application</th>
                      <th>Attester</th>
                      <th>Attester Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map((user, index) => {
                      return (
                        <tr key={index} className={user.no_attester ? 'table-danger' : ''}>
                          <td>
                            <strong>{user.database_name}</strong>
                            <br />
                            <small className="text-muted">{user.database_type}</small>
                          </td>
                          {isMssql && (
                            <td>
                              {user.physical_databases
                                ? user.physical_databases.split(',').map((db, i) => (
                                    <span key={i} className="badge bg-light text-dark border me-1 mb-1" style={{ fontWeight: 400 }}>
                                      {db.trim()}
                                    </span>
                                  ))
                                : <span className="text-muted">—</span>}
                            </td>
                          )}
                          <td>
                            <strong>{user.db_username}</strong>
                            <br />
                            <small className={
                              user.account_status === 'OPEN' || user.account_status === 'ACTIVE' ? 'text-success' :
                              user.account_status === 'LOCKED' ? 'text-danger' : 'text-muted'
                            }>
                              {user.account_status}
                            </small>
                          </td>
                          <td>
                            {user.user_display_name && user.user_display_name !== 'Unknown User' ? (
                              <>
                                <strong>{user.user_display_name}</strong>
                                <br />
                                <small className="text-muted">{user.user_email || ''}</small>
                              </>
                            ) : (
                              // Show account type for non-human accounts
                              user.is_system_account || user.attester_type === 'Database Owner' ? (
                                <span className="text-secondary">
                                  <i className="fas fa-cog me-1"></i>System Account
                                </span>
                              ) : user.attester_type === 'Account Owner' ? (
                                <span className="text-info">
                                  <i className="fas fa-robot me-1"></i>Service Account
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )
                            )}
                          </td>
                          <td>
                            {user.user_department && user.user_department !== 'Unknown Dept' ? user.user_department : <span className="text-muted">N/A</span>}
                          </td>
                          <td>
                            {user.application_name ? (
                              <>
                                <strong>{user.application_name}</strong>
                                <br />
                                <small className="text-muted">{user.application_id}</small>
                              </>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {user.no_attester ? (
                              <span className="text-danger fw-bold">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                No attester assigned
                              </span>
                            ) : (
                              <>
                                {user.manager_name || '-'}
                                <br />
                                <small className="text-muted">{user.manager_email || ''}</small>
                              </>
                            )}
                          </td>
                          <td>
                            {user.no_attester ? (
                              <span className="text-danger">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                Unassigned
                              </span>
                            ) : user.attester_type ? (
                              <span className={
                                user.attester_type === 'User Manager' ? 'text-success' :
                                user.attester_type === 'Account Owner' ? 'text-warning' :
                                user.attester_type === 'Database Owner' ? 'text-primary' :
                                user.attester_type === 'Application Owner' ? 'text-info' : 'text-secondary'
                              }>
                                <i className={`fas ${
                                  user.attester_type === 'User Manager' ? 'fa-user-tie' :
                                  user.attester_type === 'Account Owner' ? 'fa-user-shield' :
                                  user.attester_type === 'Database Owner' ? 'fa-database' :
                                  user.attester_type === 'Application Owner' ? 'fa-briefcase' : 'fa-question'
                                } me-1`}></i>
                                {user.attester_type}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, users.length)} of {users.length} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                        ) {
                          return (
                            <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pageNumber)}
                              >
                                {pageNumber}
                              </button>
                            </li>
                          );
                        } else if (
                          pageNumber === currentPage - 3 ||
                          pageNumber === currentPage + 3
                        ) {
                          return (
                            <li key={pageNumber} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}

              {/* Attestation Status Summary */}
              {attestationSubmitted && (
                <div className="mt-4">
                  <div className="alert alert-success" style={{ display: 'block' }}>
                    <h6 className="alert-heading">
                      <i className="fas fa-paper-plane me-2"></i>
                      Attestation Submitted
                    </h6>
                    <p className="mb-0">Attestation requests have been sent to attesters. Waiting for their responses.</p>
                  </div>

                  {isAttestationComplete() && (
                    <div className="alert alert-success mt-3" style={{ display: 'block' }}>
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>All attestations completed!</strong> The attestation phase is now complete.
                    </div>
                  )}
                </div>
              )}
    </div>
  );
};

export default AttestationPhase;
